/**
 * RooftopSwitcher
 *
 * Compact dropdown rendered in the TopNav. Only visible when the current user
 * has access to MORE THAN ONE rooftop (i.e. backend returns switchable: true).
 * Single-rooftop users (and master admin / reseller) see absolutely nothing.
 *
 * On select, calls POST /api/auth/switch-rooftop, replaces the stored JWT,
 * and reloads the page so all dealer-scoped data refetches under the new
 * business_id context.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDown, Building2, Check } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

export const RooftopSwitcher = () => {
  const { apiFetch, user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const wrapperRef = useRef(null);

  const loadRooftops = useCallback(async () => {
    try {
      const res = await apiFetch("/me/rooftops", { method: "GET" });
      setData(res.data);
    } catch (e) {
      // Silently ignore — dealer users without access just see nothing
      setData(null);
    }
  }, [apiFetch]);

  useEffect(() => {
    // Only fetch for dealer-side users (no point hitting the API for master/reseller)
    const role = user?.role;
    if (role === "DEALER_ADMIN" || role === "DEALER_USER") {
      loadRooftops();
    }
  }, [user?.role, loadRooftops]);

  // Close on outside click / escape
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // EXPLICITLY return null when single-rooftop. No layout impact whatsoever.
  if (!data || !data.switchable || !data.rooftops || data.rooftops.length < 2) {
    return null;
  }

  const active = data.rooftops.find((r) => r.id === data.active_business_id) || data.rooftops[0];

  const handleSwitch = async (rooftopId) => {
    if (rooftopId === data.active_business_id || switching) return;
    setSwitching(true);
    try {
      const res = await apiFetch("/auth/switch-rooftop", {
        method: "POST",
        data: { business_id: rooftopId },
      });
      const newToken = res.data?.access_token;
      if (newToken) {
        localStorage.setItem("token", newToken);
        // Hard reload so all data, /me, sidebar, dashboards re-fetch under the
        // newly-active rooftop.
        window.location.reload();
      }
    } catch (e) {
      setSwitching(false);
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Could not switch rooftop. Please try again.");
    }
  };

  return (
    <div className="rooftop-switcher" ref={wrapperRef} data-testid="rooftop-switcher">
      <button
        type="button"
        className="rooftop-switcher-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Active rooftop: ${active.name}`}
        data-testid="rooftop-switcher-trigger"
      >
        <Building2 size={14} className="rooftop-switcher-icon" />
        <span className="rooftop-switcher-name" data-testid="rooftop-switcher-active-name">
          {active.name}
        </span>
        <ChevronDown size={14} className={`rooftop-switcher-chev ${open ? "is-open" : ""}`} />
      </button>

      {open && (
        <div className="rooftop-switcher-menu" role="menu" data-testid="rooftop-switcher-menu">
          <div className="rooftop-switcher-menu-header">Switch rooftop</div>
          {data.rooftops.map((r) => {
            const isActive = r.id === data.active_business_id;
            return (
              <button
                key={r.id}
                type="button"
                className={`rooftop-switcher-item ${isActive ? "is-active" : ""}`}
                onClick={() => handleSwitch(r.id)}
                disabled={switching}
                data-testid={`rooftop-switcher-item-${r.id}`}
                role="menuitem"
              >
                <span className="rooftop-switcher-item-main">
                  <span className="rooftop-switcher-item-name">{r.name}</span>
                  {r.is_primary && (
                    <span className="rooftop-switcher-primary-tag">Primary</span>
                  )}
                  {r.franchise_make && (
                    <span className="rooftop-switcher-franchise">{r.franchise_make}</span>
                  )}
                </span>
                {isActive && <Check size={14} className="rooftop-switcher-check" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RooftopSwitcher;
